/**
 * Launch Mode Configuration
 * 
 * Controls whether the app is in "closed beta" or "production" mode.
 * 
 * In BETA mode:
 * - Landing page shows waitlist/invite-only messaging
 * - Signup requires a valid invite code
 * - CTAs emphasize exclusivity and early access
 * 
 * In PRODUCTION mode:
 * - Open registration (current behavior)
 * - Standard signup flow
 * - Normal CTAs
 * 
 * To switch modes:
 * - Set VITE_LAUNCH_MODE environment variable to 'beta' or 'production'
 * - Or modify the default value below for quick testing
 */

export type LaunchMode = 'beta' | 'production';

// Default to 'beta' for closed beta launch, change to 'production' when ready
const DEFAULT_MODE: LaunchMode = 'beta';

export const LAUNCH_MODE: LaunchMode = 
  (import.meta.env.VITE_LAUNCH_MODE as LaunchMode) || DEFAULT_MODE;

/**
 * Check if the app is in closed beta mode
 */
export const isBetaMode = (): boolean => LAUNCH_MODE === 'beta';

/**
 * Check if the app is in production (open registration) mode
 */
export const isProductionMode = (): boolean => LAUNCH_MODE === 'production';

/**
 * Beta mode configuration
 */
export const BETA_CONFIG = {
  // Hero section
  heroBadge: '🚀 Open Beta',
  heroSubtext: 'Free for all users',

  // CTA text
  primaryCta: 'Get Started Free',
  primaryCtaWithCode: 'Redeem Your Code',
  secondaryCta: 'See How It Works',

  // Trust bar
  trustText: 'Join 1,000+ professionals building careers with intention',

  // Signup messaging
  signupTitle: 'Create your account',
  signupDescription: 'Start your values-driven career journey — free during beta.',
  noCodeMessage: 'Don\'t have an invite code? Join our waitlist and we\'ll notify you when spots open up.',

  // Invite code prompt
  inviteCodePrompt: '🎟️ Have an invite code?',
  inviteCodeLink: 'Enter Code',

  // Footer/badge
  betaBadgeText: 'Open Beta',

  // Signup gating — set to true to require invite codes for individual signup
  requireInviteForSignup: false,
};

/**
 * Production mode configuration (for reference)
 */
export const PRODUCTION_CONFIG = {
  heroBadge: null,
  heroSubtext: null,
  primaryCta: 'Start Your 5-Minute Assessment',
  secondaryCta: 'See How It Works',
  trustText: 'Join 2,000+ professionals building careers with intention, not guesswork',
  signupTitle: 'Create your account',
  signupDescription: 'Start your career journey today.',
};

/**
 * Get the appropriate config based on current mode
 */
export const getLaunchConfig = () => {
  return isBetaMode() ? BETA_CONFIG : PRODUCTION_CONFIG;
};
